import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';
import {
  createRoad,
  deleteRoad,
  fetchDetailRoadStream,
  fetchRoadList,
  updateRoad,
} from '@/shared/apis/road-management';
import type { RoadData } from '../types/types-road-management';

const useRoadManagement = ({
  roadIdParams = null,
  queryParams = '',
  onUpdateSuccess = (_responseData: unknown) => {},
  onDeleteSuccess = () => {},
  onCreateSuccess = (_responseData: unknown) => {},
}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Query to fetch road list
  const { data: roadListData, isLoading: isLoadingRoad } = useQuery({
    queryKey: ['roadListData', queryParams],
    queryFn: () => fetchRoadList(queryParams),
    staleTime: 0,
    enabled: queryParams !== '', // queryParams가 없으면 요청이 활성화되지 않음
  });

  // Query to fetch road details stream url
  const { data: roadDetailStreamUrl } = useQuery({
    queryKey: ['roadDetailStreamUrl', roadIdParams],
    // always will be a number when activated
    queryFn: () => fetchDetailRoadStream(roadIdParams as unknown as number),
    retry: false,
    enabled: !!roadIdParams,
  });

  // 콘솔에 출력 (useEffect로 감싸지 않고 바로 출력)
  if (roadListData) {
    console.log('도로 목록 데이터:', roadListData);
  }

  // delete road
  const deleteRoadMutation = useMutation({
    mutationFn: (roadId: number) => deleteRoad(roadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roadListData'] });
      queryClient.invalidateQueries({ queryKey: ['siteListData'] });
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > delete success'), {
        callback() {
          onDeleteSuccess();
        },
      });
    },
    onError: (err) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(err.message));
    },
  });

  // create road
  // Mutation to create a road (POST request)
  const createRoadMutation = useMutation({
    mutationFn: (roadData: RoadData) => createRoad(roadData),
    onSuccess: (responseData) => {
      queryClient.invalidateQueries({ queryKey: ['roadListData'] });
      queryClient.invalidateQueries({ queryKey: ['siteListData'] });
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > registration success'), {
        callback() {
          onCreateSuccess(responseData);
        },
      });
    },
    onError: (err) => {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(err.message));
    },
  });

  // update road
  // Mutation to update a rad (PUT request)
  const updateRoadMutation = useMutation({
    mutationFn: (roadData: RoadData) => updateRoad(roadData.site_id, roadData),
    onSuccess: (responseData) => {
      queryClient.invalidateQueries({ queryKey: ['roadListData'] });
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > update success'), {
        callback() {
          onUpdateSuccess(responseData);
        },
      });
    },
    onError: (err) => {
      console.error('Error updating road:', err);
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t(err.message));
    },
  });

  return {
    roadListData,
    isLoadingRoad,
    roadDetailStreamUrl,
    createRoad: createRoadMutation.mutate,
    deleteRoad: deleteRoadMutation.mutate,
    updateRoad: updateRoadMutation.mutate,
  };
};

export default useRoadManagement;
